/**
 * Sidebar - config-driven, collapsible left navigation.
 *
 * Renders generically from the supplied navigation config. Three kinds:
 *   - 'group'  : grey collapsible header + white child rows.
 *   - 'link'   : white nav row with a leading icon (rendered as a <button> so it
 *                matches groups exactly and Backstage's global <a> styles can't
 *                strip the collapsed-rail tile).
 *   - 'custom' : freeform widget (e.g. LanguageSwitcher). Renders inline when
 *                expanded; shows a collapsedIcon tile (expands sidebar) when
 *                collapsed. `placement: 'footer'` pins it above the footer.
 */
import { ElementType, useState, useEffect, useRef, useMemo } from 'react';
import { NavLink, Link, useNavigate, useLocation } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import Tooltip from '@material-ui/core/Tooltip';
import IconButton from '@material-ui/core/IconButton';
import Divider from '@material-ui/core/Divider';
import MenuIcon from '@material-ui/icons/Menu';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import AddIcon from '@material-ui/icons/Add';
import { NavSelectableProject, ShellNavConfig } from './navTypes';
import { useTranslation } from 'react-i18next';
import CheckIcon from '@material-ui/icons/Check';

const RAIL_OPEN = 288;
const RAIL_CLOSED = 72;
const HEADER_H = 64;
const SQUARE = 44;
const TEXT_INDENT = 30;

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    background: theme.palette.background.paper,
    borderRight: `1px solid ${theme.palette.divider}`,
    transition: 'width 160ms ease',
    overflowX: 'hidden',
  },
  open: { width: RAIL_OPEN, minWidth: RAIL_OPEN },
  closed: { width: RAIL_CLOSED, minWidth: RAIL_CLOSED },

  header: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    height: HEADER_H,
    minHeight: HEADER_H,
    padding: theme.spacing(0, 1.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
    boxSizing: 'border-box',
  },
  hamburger: {
    color: theme.palette.text.secondary,
    '&:hover': { color: theme.palette.text.primary },
    '&:focus-visible': {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: 2,
    },
  },
  brandLink: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.25),
    minWidth: 0,
    flex: 1,
    textDecoration: 'none',
    borderRadius: theme.shape.borderRadius,
    '&:focus-visible': {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: 2,
    },
  },
  brandLogo: {
    width: 36,
    height: 36,
    minWidth: 36,
    borderRadius: theme.shape.borderRadius * 2,
    background: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 700,
    fontSize: '0.82rem',
  },
  brandText: { display: 'flex', flexDirection: 'column', minWidth: 0 },
  brandTitle: {
    fontSize: '0.9rem',
    fontWeight: 600,
    lineHeight: 1.25,
    color: theme.palette.text.primary,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  brandSub: {
    fontSize: '0.7rem',
    lineHeight: 1.25,
    color: theme.palette.text.secondary,
    whiteSpace: 'nowrap',
  },

  scroll: {
    flex: 1,
    overflowY: 'auto',
    overflowX: 'hidden',
    padding: theme.spacing(1.5, 1),
  },

  workspaceRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(0, 1),
    marginBottom: theme.spacing(1),
  },
  workspaceLabel: {
    fontSize: '0.7rem',
    fontWeight: 700,
    lineHeight: 1.35,
    letterSpacing: 0.8,
    textTransform: 'uppercase',
    color: theme.palette.text.secondary,
  },
  addBtn: {
    width: 26,
    height: 26,
    padding: 0,
    color: theme.palette.primary.contrastText,
    background: theme.palette.primary.main,
    '&:hover': { background: theme.palette.primary.dark },
    '&:focus-visible': {
      outline: `2px solid ${theme.palette.secondary.main}`,
      outlineOffset: 2,
    },
  },
  addIcon: { fontSize: '1.05rem' },

  groupHeader: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    height: 32,
    padding: 0,
    marginTop: theme.spacing(1),
    background: 'none',
    border: 'none',
    borderRadius: theme.shape.borderRadius,
    color: theme.palette.text.secondary,
    fontSize: '0.7rem',
    fontWeight: 700,
    lineHeight: 1.35,
    letterSpacing: 0.8,
    textTransform: 'uppercase',
    fontFamily: 'inherit',
    textAlign: 'left',
    cursor: 'pointer',
    '&:hover': { background: theme.palette.action.hover },
    '&:focus-visible': {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: -2,
    },
  },
  caretGutter: {
    width: TEXT_INDENT,
    minWidth: TEXT_INDENT,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  caret: { fontSize: '1.1rem', color: theme.palette.text.secondary },
  groupTitle: { flex: 1 },
  groupPill: { marginRight: theme.spacing(1) },

  pill: {
    minWidth: 20,
    height: 20,
    padding: theme.spacing(0, 0.75),
    borderRadius: 10,
    background: theme.palette.action.selected,
    color: theme.palette.text.secondary,
    fontSize: '0.68rem',
    fontWeight: 600,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  pillAccent: {
    color: theme.palette.secondary.main,
    textTransform: 'capitalize',
  },

  row: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    height: 42,
    width: '100%',
    paddingRight: theme.spacing(1.5),
    margin: theme.spacing(0.25, 0),
    borderRadius: theme.shape.borderRadius * 1.5,
    fontSize: '0.86rem',
    lineHeight: 1.35,
    fontFamily: 'inherit',
    color: theme.palette.text.primary,
    textDecoration: 'none',
    cursor: 'pointer',
    boxSizing: 'border-box',
    background: 'none',
    border: 'none',
    textAlign: 'left',
    transition: 'background 120ms ease',
    '&:hover': { background: theme.palette.action.hover },
    '&:focus-visible': {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: -2,
    },
  },
  rowChild: { paddingLeft: TEXT_INDENT },
  rowLink: { paddingLeft: theme.spacing(1) },
  rowActive: {
    background: theme.palette.action.selected,
    fontWeight: 600,
    '&::before': {
      content: '""',
      position: 'absolute',
      left: 0,
      top: 7,
      bottom: 7,
      width: 3,
      borderRadius: 3,
      background: theme.palette.secondary.main,
    },
  },
  rowIcon: {
    width: 22,
    minWidth: 22,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.palette.text.secondary,
    flexShrink: 0,
    fontSize: '1.15rem',
  },
  rowName: {
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },

  projectsEntry: {
    position: 'relative',
    margin: theme.spacing(0.25, 0),
  },

  projectsMainRow: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
  },

  projectsLink: {
    position: 'relative',
    flex: 1,
    minWidth: 0,
    height: 42,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(0.5),
    borderRadius: theme.shape.borderRadius * 1.5,
    color: theme.palette.text.primary,
    textDecoration: 'none',
    boxSizing: 'border-box',

    '&:hover': {
      background: theme.palette.action.hover,
    },

    '&:focus-visible': {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: -2,
    },
  },

  projectsLinkActive: {
    background: theme.palette.action.selected,
    fontWeight: 600,

    '&::before': {
      content: '""',
      position: 'absolute',
      left: 0,
      top: 7,
      bottom: 7,
      width: 3,
      borderRadius: 3,
      background: theme.palette.secondary.main,
    },
  },

  projectsDropdownButton: {
    width: 38,
    minWidth: 38,
    height: 42,
    padding: 0,
    border: 'none',
    borderRadius: theme.shape.borderRadius,
    background: 'transparent',
    color: theme.palette.text.secondary,
    cursor: 'pointer',

    '&:hover': {
      background: theme.palette.action.hover,
    },

    '&:focus-visible': {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: -2,
    },
  },

  projectSelector: {
    position: 'absolute',
    top: 44,
    left: theme.spacing(1),
    right: theme.spacing(1),
    zIndex: 30,
    padding: theme.spacing(1),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius * 1.5,
    background: theme.palette.background.paper,
    boxShadow: '0 10px 28px rgba(0, 0, 0, 0.16)',
  },

  projectSelectorTitle: {
    padding: theme.spacing(0.5, 1, 1),
    color: theme.palette.text.secondary,
    fontSize: '0.7rem',
    fontWeight: 700,
    letterSpacing: 0.7,
    textTransform: 'uppercase',
  },

  projectOption: {
    width: '100%',
    minHeight: 42,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(0, 1),
    border: 'none',
    borderRadius: theme.shape.borderRadius,
    background: 'transparent',
    color: theme.palette.text.primary,
    fontFamily: 'inherit',
    textAlign: 'left',
    cursor: 'pointer',

    '&:hover': {
      background: theme.palette.action.hover,
    },

    '&:focus-visible': {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: -2,
    },
  },

  projectOptionSelected: {
    background: theme.palette.action.selected,
    color: theme.palette.secondary.main,
  },

  projectOptionName: {
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    fontSize: '0.82rem',
    fontWeight: 600,
  },

  projectCheckIcon: {
    fontSize: '1rem',
    color: theme.palette.secondary.main,
  },

  selectedProjectTree: {
    marginTop: theme.spacing(0.5),
    marginLeft: theme.spacing(2.5),
    paddingLeft: theme.spacing(2),
    paddingBottom: theme.spacing(0.5),
    borderLeft: `2px solid ${theme.palette.divider}`,
  },

  selectedProjectRow: {
    position: 'relative',
    width: '100%',
    minHeight: 40,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(0, 1),
    border: 'none',
    borderRadius: theme.shape.borderRadius * 1.5,
    background: theme.palette.action.hover,
    color: theme.palette.text.primary,
    fontFamily: 'inherit',
    textAlign: 'left',
  },

  selectedProjectIcon: {
    width: 18,
    minWidth: 18,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.palette.text.secondary,

    '& svg': {
      fontSize: '1rem',
    },
  },

  selectedProjectName: {
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    fontSize: '0.82rem',
    fontWeight: 700,
  },

  projectChildLink: {
    position: 'relative',
    width: '100%',
    minHeight: 38,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(0, 1),
    paddingLeft: theme.spacing(2),
    marginTop: theme.spacing(0.25),
    borderRadius: theme.shape.borderRadius * 1.5,
    color: theme.palette.text.secondary,
    textDecoration: 'none',
    fontSize: '0.8rem',
    boxSizing: 'border-box',

    '&::before': {
      content: '""',
      position: 'absolute',
      left: -17,
      top: '50%',
      width: 13,
      borderTop: `1px solid ${theme.palette.divider}`,
    },

    '&:hover': {
      color: theme.palette.text.primary,
      background: theme.palette.action.hover,
    },

    '&:focus-visible': {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: -2,
    },
  },

  projectChildActive: {
    color: theme.palette.secondary.main,
    background: theme.palette.action.selected,
    fontWeight: 700,
  },

  projectChildIcon: {
    width: 18,
    minWidth: 18,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',

    '& svg': {
      fontSize: '1rem',
    },
  },

  createRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    width: '100%',
    height: 38,
    paddingLeft: TEXT_INDENT,
    paddingRight: theme.spacing(1.5),
    margin: theme.spacing(0.25, 0),
    borderRadius: theme.shape.borderRadius * 1.5,
    background: 'none',
    border: 'none',
    fontFamily: 'inherit',
    fontSize: '0.82rem',
    fontWeight: 600,
    lineHeight: 1.35,
    color: theme.palette.primary.main,
    cursor: 'pointer',
    textAlign: 'left',
    '&:hover': { background: theme.palette.action.hover },
    '&:focus-visible': {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: -2,
    },
  },
  createIcon: { fontSize: '1.1rem' },

  // Wrapper for a custom widget entry (expanded).
  customEntry: { padding: theme.spacing(0.5, 0) },

  railWrap: { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' },
  rail: {
    flex: 1,
    overflowY: 'auto',
    overflowX: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(1.25, 0),
  },
  railBottom: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(1, 0),
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  square: {
    width: SQUARE,
    height: SQUARE,
    minWidth: SQUARE,
    borderRadius: theme.shape.borderRadius * 2,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxSizing: 'border-box',
    margin: 0,
    padding: 0,
    border: 'none',
    outline: 'none',
    appearance: 'none',
    font: 'inherit',
    // Equal, subtly-visible TILE (no border) so mixed glyphs read uniformly.
    background: theme.palette.action.hover,
    color: theme.palette.text.secondary,
    textDecoration: 'none',
    cursor: 'pointer',
    position: 'relative',
    '& svg': {
      fontSize: '1.3rem',
      width: '1.3rem',
      height: '1.3rem',
      display: 'block',
    },
    transition: 'background 120ms ease, color 120ms ease',
    '&:hover': {
      background: theme.palette.action.selected,
      color: theme.palette.text.primary,
    },
    '&:focus-visible': {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: 2,
    },
  },
  squarePlain: {
    background: 'transparent',
    '&:hover': { background: theme.palette.action.hover },
  },
  squareBrand: {
    background: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    fontWeight: 700,
    fontSize: '0.78rem',
    '&:hover': {
      background: theme.palette.primary.dark,
      color: theme.palette.primary.contrastText,
    },
  },
  squareActive: {
    background: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    '&:hover': {
      background: theme.palette.primary.dark,
      color: theme.palette.primary.contrastText,
    },
    '&::before': {
      content: '""',
      position: 'absolute',
      left: -6,
      top: 10,
      bottom: 10,
      width: 3,
      borderRadius: 3,
      background: theme.palette.secondary.main,
    },
  },
  railDivider: { width: 28, margin: theme.spacing(0.5, 0) },

  footer: {
    borderTop: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(1.5, 2),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    width: '100%',
    background: 'none',
    border: 'none',
    fontFamily: 'inherit',
    fontSize: '0.82rem',
    color: theme.palette.text.secondary,
    cursor: 'pointer',
    textAlign: 'left',
    '&:hover': { color: theme.palette.text.primary },
    '&:focus-visible': {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: -2,
    },
  },
  footerIcon: { fontSize: '1.2rem', color: theme.palette.text.secondary },
}));

export type SidebarProps = {
  nav: ShellNavConfig;
  onCreate?: () => void;
  createTooltip?: string;
  footer?: { icon: ElementType; label: string; onClick: () => void };
};

export function Sidebar({
  nav,
  onCreate,
  createTooltip,
  footer,
}: SidebarProps) {
  const classes = useStyles();
  const { t } = useTranslation('shell');
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(true);
  const projectSelectorRef = useRef<HTMLDivElement | null>(null);

  const [projectSelectorOpen, setProjectSelectorOpen] = useState(false);
  const [groupOpen, setGroupOpen] = useState<Record<string, boolean>>(() =>
    nav.entries.reduce((acc, entry) => {
      if (entry.kind === 'group') {
        acc[entry.id] = entry.defaultOpen ?? false;
      }

      return acc;
    }, {} as Record<string, boolean>),
  );

  // eslint-disable-next-line security/detect-object-injection
  const toggleGroup = (id: string) =>
    setGroupOpen(s => ({ ...s, [id]: !s[id] }));
  const expandAndOpen = (id: string) => {
    setOpen(true);
    setGroupOpen(s => ({ ...s, [id]: true }));
  };

  const projectsEntry = nav.entries.find(
    entry => entry.kind === 'projects-link',
  );

  const availableProjects = useMemo(
    () =>
      projectsEntry?.kind === 'projects-link' ? projectsEntry.projects : [],
    [projectsEntry],
  );

  const projectIdFromRoute = pathname.match(/^\/dashboard\/([^/]+)/)?.[1];

  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(
    () => {
      if (
        projectIdFromRoute &&
        availableProjects.some(project => project.id === projectIdFromRoute)
      ) {
        return projectIdFromRoute;
      }

      if (typeof window !== 'undefined') {
        try {
          return window.sessionStorage.getItem('ses-active-sidebar-project');
        } catch {
          return null;
        }
      }

      return null;
    },
  );

  const selectedProject =
    availableProjects.find(project => project.id === selectedProjectId) ??
    availableProjects[0] ??
    null;

  useEffect(() => {
    if (
      projectIdFromRoute &&
      availableProjects.some(project => project.id === projectIdFromRoute)
    ) {
      setSelectedProjectId(projectIdFromRoute);
      return;
    }

    if (
      selectedProjectId &&
      availableProjects.some(project => project.id === selectedProjectId)
    ) {
      return;
    }

    setSelectedProjectId(availableProjects[0]?.id ?? null);
  }, [availableProjects, projectIdFromRoute, selectedProjectId]);

  useEffect(() => {
    if (typeof window === 'undefined' || !selectedProjectId) {
      return;
    }
    try {
      window.sessionStorage.setItem(
        'ses-active-sidebar-project',
        selectedProjectId,
      );
    } catch {
      // Ignore storage failures
    }
  }, [selectedProjectId]);

  useEffect(() => {
    if (!projectSelectorOpen) {
      return undefined;
    }

    const handleDocumentClick = (event: MouseEvent) => {
      if (
        projectSelectorRef.current &&
        !projectSelectorRef.current.contains(event.target as Node)
      ) {
        setProjectSelectorOpen(false);
      }
    };

    document.addEventListener('mousedown', handleDocumentClick);

    return () => {
      document.removeEventListener('mousedown', handleDocumentClick);
    };
  }, [projectSelectorOpen]);

  const handleSelectProject = (project: NavSelectableProject) => {
    setSelectedProjectId(project.id);
    setProjectSelectorOpen(false);

    navigate(`/dashboard/${project.id}/test-case-generation`);
  };

  const isActive = (path: string) =>
    pathname === path || pathname.startsWith(`${path}/`);

  const rowClass =
    (variant: 'child' | 'link') =>
    ({ isActive: a }: { isActive: boolean }) =>
      `${classes.row} ${
        variant === 'child' ? classes.rowChild : classes.rowLink
      } ${a ? classes.rowActive : ''}`;

  const pillClass = (item: {
    pill?: {
      label: string;
      tone?: 'default' | 'accent';
    };
  }) =>
    `${classes.pill} ${item.pill?.tone === 'accent' ? classes.pillAccent : ''}`;

  return (
    <nav
      id="app-sidebar"
      className={`${classes.root} ${open ? classes.open : classes.closed}`}
      aria-label={t('navigation', { title: nav.brand.title })}
    >
      {open ? (
        <>
          <div className={classes.header}>
            <Tooltip title={t('collapseSidebar')} placement="right">
              <IconButton
                size="small"
                className={classes.hamburger}
                onClick={() => setOpen(false)}
                aria-label={t('collapseSidebar')}
                aria-expanded
                aria-controls="app-sidebar"
              >
                <MenuIcon />
              </IconButton>
            </Tooltip>
            <Link
              to={nav.brand.homePath}
              className={classes.brandLink}
              aria-label={t('goHome')}
            >
              <span className={classes.brandLogo} aria-hidden="true">
                {nav.brand.short}
              </span>
              <span className={classes.brandText}>
                <span className={classes.brandTitle}>{nav.brand.title}</span>
                {nav.brand.subtitle && (
                  <span className={classes.brandSub}>{nav.brand.subtitle}</span>
                )}
              </span>
            </Link>
          </div>

          <div className={classes.scroll}>
            {nav.brand.workspaceLabel && (
              <div className={classes.workspaceRow}>
                <span className={classes.workspaceLabel}>
                  {nav.brand.workspaceLabel}
                </span>
                {onCreate && (
                  <Tooltip title={createTooltip ?? t('createProject')}>
                    <IconButton
                      size="small"
                      className={classes.addBtn}
                      aria-label={createTooltip ?? t('createProject')}
                      onClick={onCreate}
                    >
                      <AddIcon className={classes.addIcon} />
                    </IconButton>
                  </Tooltip>
                )}
              </div>
            )}

            {nav.entries.map(entry => {
              if (entry.kind === 'projects-link') {
                const projectsPageActive =
                  pathname === entry.path ||
                  pathname.startsWith(`${entry.path}/`) ||
                  pathname.startsWith('/dashboard/');

                return (
                  <div
                    key={entry.id}
                    ref={projectSelectorRef}
                    className={classes.projectsEntry}
                  >
                    <div className={classes.projectsMainRow}>
                      <NavLink
                        to={entry.path}
                        title={entry.label}
                        className={`${classes.projectsLink} ${
                          projectsPageActive ? classes.projectsLinkActive : ''
                        }`}
                      >
                        <span className={classes.rowIcon}>
                          <entry.icon fontSize="inherit" />
                        </span>

                        <span className={classes.rowName}>{entry.label}</span>
                      </NavLink>

                      <button
                        type="button"
                        className={classes.projectsDropdownButton}
                        aria-label={t('selectActiveProject')}
                        title={t('selectActiveProject')}
                        aria-expanded={projectSelectorOpen}
                        onClick={() =>
                          setProjectSelectorOpen(previous => !previous)
                        }
                      >
                        {projectSelectorOpen ? (
                          <ExpandMoreIcon fontSize="small" />
                        ) : (
                          <ChevronRightIcon fontSize="small" />
                        )}
                      </button>
                    </div>

                    {projectSelectorOpen && (
                      <div className={classes.projectSelector}>
                        <div className={classes.projectSelectorTitle}>
                          {t('selectActiveProject')}
                        </div>

                        {entry.projects.length === 0 ? (
                          <div className={classes.projectSelectorTitle}>
                            {t('noProjectsAvailable')}
                          </div>
                        ) : (
                          entry.projects.map(project => {
                            const selected = project.id === selectedProject?.id;

                            return (
                              <button
                                key={project.id}
                                type="button"
                                className={`${classes.projectOption} ${
                                  selected ? classes.projectOptionSelected : ''
                                }`}
                                onClick={() => handleSelectProject(project)}
                              >
                                <span className={classes.projectOptionName}>
                                  {project.label}
                                </span>

                                {selected && (
                                  <CheckIcon
                                    className={classes.projectCheckIcon}
                                  />
                                )}
                              </button>
                            );
                          })
                        )}
                      </div>
                    )}

                    {selectedProject && (
                      <div className={classes.selectedProjectTree}>
                        <div className={classes.selectedProjectRow}>
                          <span className={classes.selectedProjectIcon}>
                            <entry.icon fontSize="inherit" />
                          </span>

                          <span className={classes.selectedProjectName}>
                            {selectedProject.label}
                          </span>

                          {selectedProject.pill && (
                            <span className={pillClass(selectedProject)}>
                              {selectedProject.pill.label}
                            </span>
                          )}
                        </div>

                        {selectedProject.children.map(child => {
                          const ChildIcon = child.icon;

                          return (
                            <NavLink
                              key={child.id}
                              to={child.path}
                              title={child.label}
                              className={({ isActive: active }) =>
                                `${classes.projectChildLink} ${
                                  active ? classes.projectChildActive : ''
                                }`
                              }
                            >
                              {ChildIcon && (
                                <span className={classes.projectChildIcon}>
                                  <ChildIcon />
                                </span>
                              )}

                              <span className={classes.rowName}>
                                {child.label}
                              </span>
                            </NavLink>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              }
              if (entry.kind === 'group') {
                return (
                  <div key={entry.id}>
                    <button
                      type="button"
                      className={classes.groupHeader}
                      onClick={() => toggleGroup(entry.id)}
                      aria-expanded={!!groupOpen[entry.id]}
                      aria-controls={`nav-group-${entry.id}`}
                    >
                      <span className={classes.caretGutter}>
                        {groupOpen[entry.id] ? (
                          <ExpandMoreIcon className={classes.caret} />
                        ) : (
                          <ChevronRightIcon className={classes.caret} />
                        )}
                      </span>
                      <span className={classes.groupTitle}>{entry.label}</span>
                      <span className={`${classes.pill} ${classes.groupPill}`}>
                        {entry.count ?? entry.children.length}
                      </span>
                    </button>

                    {groupOpen[entry.id] && (
                      <div id={`nav-group-${entry.id}`}>
                        {entry.children.map(leaf => (
                          <NavLink
                            key={leaf.id}
                            to={leaf.path}
                            title={leaf.label}
                            className={rowClass('child')}
                          >
                            <span className={classes.rowName}>
                              {leaf.label}
                            </span>
                            {leaf.pill && (
                              <span className={pillClass(leaf)}>
                                {leaf.pill.label}
                              </span>
                            )}
                          </NavLink>
                        ))}

                        {entry.onCreate && (
                          <button
                            type="button"
                            className={classes.createRow}
                            onClick={entry.onCreate}
                          >
                            <AddIcon className={classes.createIcon} />
                            {t('newKnowledgeBase')}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                );
              }

              if (entry.kind === 'link') {
                return (
                  <NavLink
                    key={entry.id}
                    to={entry.path}
                    title={entry.label}
                    className={rowClass('link')}
                  >
                    <span className={classes.rowIcon}>
                      <entry.icon fontSize="inherit" />
                    </span>
                    <span className={classes.rowName}>{entry.label}</span>
                  </NavLink>
                );
              }

              // kind === 'custom'
              return (
                <div key={entry.id} className={classes.customEntry}>
                  {entry.render()}
                </div>
              );
            })}
          </div>

          {footer && (
            <button
              type="button"
              className={classes.footer}
              onClick={footer.onClick}
            >
              <footer.icon className={classes.footerIcon} aria-hidden="true" />
              {footer.label}
            </button>
          )}
        </>
      ) : (
        <div className={classes.railWrap}>
          <div className={classes.rail}>
            <Tooltip title={t('expandSidebar')} placement="right">
              <button
                type="button"
                className={`${classes.square} ${classes.squarePlain}`}
                onClick={() => setOpen(true)}
                aria-label={t('expandSidebar')}
                aria-expanded={false}
                aria-controls="app-sidebar"
              >
                <MenuIcon fontSize="inherit" />
              </button>
            </Tooltip>

            <Tooltip title={t('home')} placement="right">
              <Link
                to={nav.brand.homePath}
                className={`${classes.square} ${classes.squareBrand}`}
                aria-label={t('goHome')}
              >
                {nav.brand.short}
              </Link>
            </Tooltip>

            <Divider className={classes.railDivider} />

            {nav.entries.map(entry => {
              if (entry.kind === 'projects-link') {
                const projectsActive =
                  pathname === entry.path ||
                  pathname.startsWith(`${entry.path}/`) ||
                  pathname.startsWith('/dashboard/');

                return (
                  <Tooltip key={entry.id} title={entry.label} placement="right">
                    <button
                      type="button"
                      aria-label={entry.label}
                      className={`${classes.square} ${
                        projectsActive ? classes.squareActive : ''
                      }`}
                      onClick={() => navigate(entry.path)}
                    >
                      <entry.icon fontSize="inherit" />
                    </button>
                  </Tooltip>
                );
              }
              if (entry.kind === 'group') {
                return (
                  <Tooltip key={entry.id} title={entry.label} placement="right">
                    <button
                      type="button"
                      className={classes.square}
                      aria-label={t('expandNavigationGroup', {
                        label: entry.label,
                      })}
                      onClick={() => expandAndOpen(entry.id)}
                    >
                      <entry.icon
                        fontSize="inherit"
                        style={
                          entry.iconScale
                            ? { transform: `scale(${entry.iconScale})` }
                            : undefined
                        }
                      />
                    </button>
                  </Tooltip>
                );
              }

              if (entry.kind === 'link') {
                return (
                  <Tooltip key={entry.id} title={entry.label} placement="right">
                    <button
                      type="button"
                      aria-label={entry.label}
                      className={`${classes.square} ${
                        isActive(entry.path) ? classes.squareActive : ''
                      }`}
                      onClick={() => navigate(entry.path)}
                    >
                      <entry.icon
                        fontSize="inherit"
                        style={
                          entry.iconScale
                            ? { transform: `scale(${entry.iconScale})` }
                            : undefined
                        }
                      />
                    </button>
                  </Tooltip>
                );
              }

              // kind === 'custom' -> a widget can't fit the rail; show an icon
              // tile that expands the sidebar (if collapsedIcon provided).
              if (!entry.collapsedIcon) return null;
              const CollapsedIcon = entry.collapsedIcon;
              return (
                <Tooltip
                  key={entry.id}
                  title={entry.collapsedTooltip ?? ''}
                  placement="right"
                >
                  <button
                    type="button"
                    className={classes.square}
                    aria-label={entry.collapsedTooltip ?? t('language')}
                    onClick={() => setOpen(true)}
                  >
                    <CollapsedIcon fontSize="inherit" />
                  </button>
                </Tooltip>
              );
            })}
          </div>

          <div className={classes.railBottom}>
            {footer && (
              <Tooltip title={footer.label} placement="right">
                <button
                  type="button"
                  className={classes.square}
                  aria-label={footer.label}
                  onClick={footer.onClick}
                >
                  <footer.icon fontSize="inherit" />
                </button>
              </Tooltip>
            )}
          </div>
        </div>
      )}
    </nav>
  );
}
